import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const LogoNav = () => (
  <header className="fixed top-0 w-full bg-white/80 backdrop-blur-xl z-50 border-b border-gray-200/50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center h-16">
        <Link to="/" className="flex items-baseline select-none">
          <span className="text-2xl font-bold tracking-tight text-gray-900">Anschluss</span>
          <span className="text-2xl font-bold tracking-tight bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">Pilot</span>
          <span className="text-2xl font-bold text-cyan-500 ml-0.5">.</span>
        </Link>
        <Link to="/" className="flex items-center text-gray-600 hover:text-cyan-600 transition-colors text-sm font-semibold" data-testid="back-to-home">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zur Startseite
        </Link>
      </div>
    </div>
  </header>
);

const PageFooter = () => (
  <footer className="bg-gray-950 text-white py-12 border-t border-white/10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row justify-between items-center">
        <div className="flex items-baseline mb-4 md:mb-0">
          <span className="text-xl font-bold tracking-tight text-white">Anschluss</span>
          <span className="text-xl font-bold tracking-tight bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Pilot</span>
          <span className="text-xl font-bold text-cyan-400 ml-0.5">.</span>
        </div>
        <div className="flex space-x-6 text-sm text-gray-400">
          <Link to="/datenschutz" className="hover:text-cyan-400 transition-colors">Datenschutz</Link>
          <Link to="/impressum" className="hover:text-cyan-400 transition-colors">Impressum</Link>
        </div>
      </div>
      <p className="text-gray-500 text-sm text-center mt-6">
        © {new Date().getFullYear()} AnschlussPilot. Alle Rechte vorbehalten.
      </p>
    </div>
  </footer>
);

export const Datenschutz = () => (
  <div className="min-h-screen bg-gray-50 flex flex-col">
    <LogoNav />
    <main className="flex-1 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight" data-testid="datenschutz-title">
          Datenschutz<span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">erklärung</span>
        </h1>
        <p className="text-gray-500 mb-12">Stand: {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</p>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 lg:p-12 space-y-8 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Verantwortlicher</h2>
            <p>
              Verantwortlich für die Datenverarbeitung auf dieser Website ist:<br />
              <strong>MR Unternehmensgruppe</strong><br />
              Röntgenstr. 9<br />
              60388 Frankfurt am Main<br />
              Deutschland<br />
              E-Mail: info@anschlusspilot.de<br />
              Telefon: +41 78 421 04 22
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Erhebung und Verarbeitung von Daten</h2>
            <p className="mb-4">
              Wir erheben personenbezogene Daten nur, wenn Sie uns diese aktiv mitteilen, z.B. über unsere Kontaktformulare („Demo anfragen", „Pilotprojekt starten").
            </p>
            <p className="mb-2"><strong>Verarbeitete Daten:</strong></p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name</li>
              <li>Unternehmen</li>
              <li>E-Mail-Adresse</li>
              <li>Telefonnummer (optional)</li>
              <li>Nachrichteninhalt</li>
            </ul>
            <p className="mt-4">
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Anbahnung eines Vertragsverhältnisses) sowie Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Kontaktaufnahme).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Speicherdauer</h2>
            <p>
              Ihre Anfragen werden so lange gespeichert, wie es zur Bearbeitung erforderlich ist. Nach Abschluss der Anfrage werden die Daten innerhalb von 12 Monaten gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Cookies & Tracking</h2>
            <p>
              Diese Website verwendet ausschließlich technisch notwendige Cookies. Es findet kein Tracking durch Drittanbieter wie Google Analytics, Facebook Pixel o.ä. statt.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Datenweitergabe</h2>
            <p>
              Eine Weitergabe Ihrer Daten an Dritte erfolgt nicht, es sei denn, wir sind gesetzlich dazu verpflichtet oder Sie haben ausdrücklich eingewilligt.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Ihre Rechte</h2>
            <p className="mb-2">Ihnen stehen folgende Rechte zu:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Auskunft über gespeicherte Daten (Art. 15 DSGVO)</li>
              <li>Berichtigung (Art. 16 DSGVO)</li>
              <li>Löschung (Art. 17 DSGVO)</li>
              <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
              <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
              <li>Widerspruch (Art. 21 DSGVO)</li>
              <li>Beschwerde bei einer Aufsichtsbehörde</li>
            </ul>
            <p className="mt-4">
              Zur Ausübung Ihrer Rechte wenden Sie sich an: <strong>info@anschlusspilot.de</strong>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Hosting</h2>
            <p>
              Diese Website wird auf Servern in der EU gehostet. Die Datenverarbeitung erfolgt DSGVO-konform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Änderungen dieser Datenschutzerklärung</h2>
            <p>
              Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen Anforderungen entspricht.
            </p>
          </section>
        </div>
      </div>
    </main>
    <PageFooter />
  </div>
);

export const Impressum = () => (
  <div className="min-h-screen bg-gray-50 flex flex-col">
    <LogoNav />
    <main className="flex-1 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-12 tracking-tight" data-testid="impressum-title">
          Impres<span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">sum</span>
        </h1>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 lg:p-12 space-y-8 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Angaben gemäß § 5 TMG</h2>
            <p>
              <strong>MR Unternehmensgruppe</strong><br />
              Röntgenstr. 9<br />
              60388 Frankfurt am Main<br />
              Deutschland
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Kontakt</h2>
            <p>
              E-Mail: <a href="mailto:info@anschlusspilot.de" className="text-cyan-600 hover:underline">info@anschlusspilot.de</a><br />
              Telefon: <a href="tel:+41784210422" className="text-cyan-600 hover:underline">+41 78 421 04 22</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV</h2>
            <p>
              MR Unternehmensgruppe<br />
              Röntgenstr. 9, 60388 Frankfurt am Main
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Streitschlichtung</h2>
            <p className="mb-4">
              Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{' '}
              <a href="https://ec.europa.eu/consumers/odr/" className="text-cyan-600 hover:underline" target="_blank" rel="noopener noreferrer">
                https://ec.europa.eu/consumers/odr/
              </a>
            </p>
            <p>
              Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Haftung für Inhalte</h2>
            <p>
              Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Urheberrecht</h2>
            <p>
              Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechts bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
            </p>
          </section>
        </div>
      </div>
    </main>
    <PageFooter />
  </div>
);
